import { Toast } from '@modusign/design-system';
import { useHosts, useSigners } from '@modusign/react-signing';
import { useSigningState, useUIState } from '@modusign/react-signing/setup';
import { Signer } from '@modusign/react-signing/types';
import { useMutation, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { DeepReadonly } from 'ts-essentials';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import { toDocumentModelForSetup } from '@app/features/apply-react-signing/libs/signing-mapper';
import { getFieldCounts } from '@app/features/apply-react-signing/libs/signing-mapper/toDocumentModel';
import { canStartInPersonSession as canUserStartImmediateSigningProgressAsHost } from '@app/features/inPerson';
import { getAuthorityQueryKey } from '@app/hooks/useAuthority';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import embeddedService from '@app/libs/embedded/embeddedService';
import redirect from '@app/libs/embedded/redirect';
import DocumentStatusPollingGeneralError from '@app/libs/errors/DocumentStatusPollingGeneralError';
import DocumentStatusPollingTimeoutError from '@app/libs/errors/DocumentStatusPollingTimeoutError';
import { recordGAEvent, triggerDataLayerValues } from '@app/store/analytics/actions';
import { contractSetupSubmitted } from '@app/store/contract-setup/actions';
import useStartImmediateSigningProgress from '@app/store/contract-setup/hooks/useStartSigningProgress';
import { getCarbonCopiesContactArray } from '@app/store/contract-setup/reducers/carbonCopies';
import {
  getIsCheckSchedule,
  getReminders,
  getScheduledDate,
} from '@app/store/contract-setup/selectors';
import { getKeywords, getRequestMethod } from '@app/store/documentSetup/selectors';
import { modalClose, modalOpen } from '@app/store/modal/actions';
import { postApiDocuments } from '@app/store/transport-layer/document/api/documents';
import {
  NewPostDocumentsParams,
  postNewDocuments,
  RoleGrant,
} from '@app/store/transport-layer/document/documents';
import { getEmail } from '@app/store/user/user/selectors';

const DOCUMENTS_PATH = '/documents';

export function useSubmitContractSetup() {
  const history = useHistory();

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();

  const requestMethod = useSelector(getRequestMethod);
  const carbonCopies = useSelector(getCarbonCopiesContactArray);
  const keywords = useSelector(getKeywords);
  const email = useSelector(getEmail);

  const scheduledDate = useSelector(getScheduledDate);
  const isCheckSchedule = useSelector(getIsCheckSchedule);
  const reminders = useSelector(getReminders);
  const isScheduled = !!scheduledDate && isCheckSchedule;
  const formattedReminders =
    reminders.map((reminder) => ({
      title: reminder.title,
      scheduleInfo: reminder.scheduleInfo,
    })) ?? [];

  const { signingState } = useSigningState();
  const { signers } = useSigners();
  const { hosts } = useHosts();
  const { ui } = useUIState();
  const { startImmediateSigningProgress } = useStartImmediateSigningProgress();

  return useMutation({
    mutationKey: ['SUBMIT_CONTRACT_SETUP'],
    mutationFn: async (param: {
      workspaceId: string;
      labelIds: Array<string> | undefined;
      roleGrants?: RoleGrant[];
    }) => {
      try {
        if (!signingState.document.pdfFile.id) {
          throw new Error('no pdf file id');
        }
        // TODO: @Juyeong-Byeon keyword setting 되는지 확인

        const postDocuments = embeddedService.isEmbedded ? postApiDocuments : postNewDocuments;

        const data = toDocumentModelForSetup(signingState);
        const { workspaceId, labelIds, roleGrants } = param;
        const { id: documentId } = await postDocuments(workspaceId, {
          carbonCopies,
          requestMethod,
          file: {
            id: signingState.document.pdfFile.id,
          },
          title: signingState.document.title,
          pdfPassword: signingState.document.pdfPassword?.trim() || null,
          participants: data.participants,
          attachments: data.attachments,
          fields: data.fields,
          fieldGroups: data.fieldGroups,
          verifications: data.verifications,
          labelIds,
          keywords: keywords || undefined,
          roleGrants,
          scheduledDate: isScheduled ? scheduledDate : null,
          reminders: formattedReminders,
        } as NewPostDocumentsParams);

        recordGAEvent(
          '서명요청하기',
          '카카오톡_참여자수',
          documentId,
          signers.filter((signer) => signer.signingMethod === 'KAKAO').length
        );
        recordGAEvent('문서', '계약설정완료');

        if (embeddedService.isEmbedded) {
          redirect(embeddedService.redirectUrl, {
            query: {
              documentId,
              eventType: 'document_started',
            },
          });
          return;
        }

        // (@Juyeong-Byeon): 바로 서명 시작하기 flow
        if (canUserStartImmediateSigningProgressAsSigner(ui.isInOrder, signers, email ?? '')) {
          const signerFromCurrentUser = signers.find((signer) => signer.contact === email);
          await startImmediateSigningProgress(
            documentId,
            signerFromCurrentUser?.name ?? '',
            'SIGNER',
            isScheduled
          );
        } else if (
          canUserStartImmediateSigningProgressAsHost({
            hosts,
            signers: signers.map(({ id, host, order }) => ({ id, hostId: host?.id, order })),
            currentSigningOrder: 1,
            currentUserContact: email,
            signings: [],
          })
        ) {
          await startImmediateSigningProgress(
            documentId,
            hosts.find((host) => host.contact === email)?.name ?? '',
            'HOST',
            isScheduled
          );
        } else {
          dispatch(modalClose());
          Toast.success({
            description: '서명 요청 알림을 전송했습니다.',
          });
        }

        dispatch(
          contractSetupSubmitted({
            documentId,
            title: signingState.document.title,
            signerCount: signers.length,
            labelIds,
            fieldCounts: getFieldCounts(signingState.fields),
            signingMethodTypes: signers.map((signer) => signer.signingMethod || 'EMAIL'),
            isInOrder: ui.isInOrder,
          })
        );
        dispatch(triggerDataLayerValues('서명요청'));
        history.push('/documents');
      } catch (error) {
        const isTimeout = error instanceof DocumentStatusPollingTimeoutError;
        dispatch(modalClose());

        const validationErrorMessages = [
          '"x" must be larger than or equal to 0',
          '"x" must be less than or equal to 1',
          '"y" must be larger than or equal to 0',
          '"y" must be less than or equal to 1',
          '"locationX" must be larger than or equal to 0',
          '"locationX" must be less than or equal to 1',
          '"locationY" must be larger than or equal to 0',
          '"locationY" must be less than or equal to 1',
        ];
        const isFieldValidationError = validationErrorMessages.includes(error.message);
        if (isFieldValidationError) {
          dispatch(
            modalOpen('CONFIRM', {
              title: '수정 필요',
              content:
                '사인/도장 입력란이 문서 크기를 벗어났습니다.\n벗어난 사인/도장 입력란의 위치를 조정해 주세요.',
            })
          );
        } else if (isTimeout) {
          // 임베디드일때는 폴링을 하지 않기 때문에 타임아웃이 일어나지 않습니다.
          Toast.success({
            // TODO: (@Clzzi,@Juyeong-Byeon): 내부에서 던지는 error 객체에서 title도 같이 넘겨주도록 수정
            title: isScheduled ? '문서가 전송 예약 되었습니다.' : '서명 요청을 시작했습니다.',
            description: error.message,
          });

          // timeout 에러가 발생할 경우 이미 서명 요청은 완료된 상태이기 때문에 내 문서로 이동합니다.
          history.push(DOCUMENTS_PATH);
        } else if (error instanceof DocumentStatusPollingGeneralError) {
          // 임베디드일때는 폴링을 하지 않기 때문에 타임아웃이 일어나지 않습니다.
          dispatch(
            modalOpen('DOCUMENT_POLLING_ERROR_ALERT_MODAL', {
              message: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
            })
          );
          // polling 중 일반 에러가 발생할 경우 이미 서명 요청은 완료된 상태이기 때문에 내 문서로 이동합니다.
          history.push(DOCUMENTS_PATH);
        } else if (error.message === 'OTP not verified') {
          // TODO(#4256): 오류 처리
          dispatch(
            modalOpen('CONFIRM', {
              title: isScheduled ? '예약 실패' : '서명 요청 실패',
              content:
                '업로드한 파일의 사용기한이 만료되어 파일을 다시 업로드 해야 합니다. 문서 목록 페이지로 이동합니다.',
            })
          );
          history.push(DOCUMENTS_PATH);
        } else {
          dispatch(
            modalOpen('CONFIRM', {
              title: isScheduled ? '예약 실패' : '서명 요청 실패',
              content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
            })
          );
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(getAuthorityQueryKey(workspaceId));
    },
  });
}

function canUserStartImmediateSigningProgressAsSigner(
  isInOrder: boolean | null,
  signers: DeepReadonly<Signer[]>,
  currentUserEmail: string
): boolean {
  const signerFromCurrentUser = signers.find((signer) => signer.contact === currentUserEmail);
  const firstSigner: Signer = signers[0];

  const isCurrentUserFirstParticipant =
    signerFromCurrentUser && firstSigner?.id === signerFromCurrentUser.id;

  return (
    signerFromCurrentUser?.signingMethod !== 'IN_PERSON' &&
    ((isInOrder && isCurrentUserFirstParticipant) || (!isInOrder && !!signerFromCurrentUser))
  );
}
